/**
 * Page-view tracking for the usage page (user menu → Usage) and the admin monitor (ported from
 * the old app's useUserActivityTracker). One row per page visit with its duration; only `?view=`
 * survives from the query string (the aggregation SQL and the monitor apply the same rule).
 *
 * The duration is the visit's *active* time (2026-10-06): only while the tab is visible, and not
 * past IDLE_AFTER_MS without input. Wall-clock time counted a tab left open overnight as a day's use.
 *
 * A visit is one address. The view's id and title are read when the visit is sent: they arrive a
 * moment after the route (the extension loads), and starting a new visit then wrote every page
 * twice, a 0-second "unknown" row and the real one. Visits under a second (a redirect, a page
 * flicked past, a tab opened in the background and closed) are not recorded.
 */
import { useEffect, useRef } from 'react'
import { getCsrfToken } from '@/api/session'

const TRACK_URL = '/api/activity/track.php'
const SESSION_KEY = 'distribuai_user_activity_session_id'

/** Reading a report without touching it still counts, up to this long after the last input. */
export const IDLE_AFTER_MS = 5 * 60 * 1000
const INPUT_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'scroll', 'touchstart'] as const
/** pointermove and scroll fire constantly; one input a second is plenty for the arithmetic. */
const INPUT_THROTTLE_MS = 1000

/** Active time of one visit, in ms: it counts while visible, up to IDLE_AFTER_MS after the last input. */
export class ActiveTimer {
    private activeMs = 0
    /** Start of the stretch not yet added. */
    private since: number
    private lastInput: number
    private visible: boolean

    constructor(now: number, visible: boolean) {
        this.since = now
        this.lastInput = now
        this.visible = visible
    }

    private settle(now: number) {
        if (this.visible) this.activeMs += Math.max(0, Math.min(now, this.lastInput + IDLE_AFTER_MS) - this.since)
        this.since = now
    }

    input(now: number) {
        this.settle(now)
        this.lastInput = now
    }

    hide(now: number) {
        this.settle(now)
        this.visible = false
    }

    /** Coming back to the tab is itself an input. */
    show(now: number) {
        this.settle(now)
        this.visible = true
        this.lastInput = now
    }

    total(now: number): number {
        this.settle(now)
        return this.activeMs
    }
}

function sessionId(): string {
    try {
        const existing = sessionStorage.getItem(SESSION_KEY)
        if (existing) return existing
        const created = `session_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
        sessionStorage.setItem(SESSION_KEY, created)
        return created
    } catch {
        return `session_${Date.now()}_ephemeral`
    }
}

function normalizeSearch(search: string): string {
    const view = new URLSearchParams(search).get('view')
    return view ? `?view=${view}` : ''
}

export function useActivityTracking({ enabled, pathname, search, routeKey, title }: { enabled: boolean; pathname: string; search: string; routeKey: string; title: string }) {
    const previous = useRef<string | null>(null)
    // The latest view id and title, read when the visit ends (see the header).
    const named = useRef({ routeKey, title })
    useEffect(() => {
        named.current = { routeKey, title }
    }, [routeKey, title])
    useEffect(() => {
        if (!enabled) return
        const startedAt = Date.now()
        const timer = new ActiveTimer(startedAt, document.visibilityState === 'visible')
        const pagePath = `${pathname}${normalizeSearch(search)}`
        const referrer = previous.current
        let sent = false
        let lastInputAt = 0
        const onInput = () => {
            const now = Date.now()
            if (now - lastInputAt < INPUT_THROTTLE_MS) return
            lastInputAt = now
            timer.input(now)
        }
        const onVisibility = () => (document.visibilityState === 'visible' ? timer.show(Date.now()) : timer.hide(Date.now()))
        const send = () => {
            if (sent) return
            sent = true
            const activeSeconds = Math.round(timer.total(Date.now()) / 1000)
            if (activeSeconds < 1) return // also StrictMode's synchronous remount in DEV
            // keepalive: the request must survive the page unloading. Tracking never blocks or throws.
            // A cookie-authenticated write: PHP wants the session's CSRF token (bootstrap.php validateUserSession).
            const csrf = getCsrfToken()
            void fetch(TRACK_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...(csrf ? { 'X-CSRF-Token': csrf } : {}) },
                credentials: 'same-origin',
                keepalive: true,
                body: JSON.stringify({
                    page_path: pagePath,
                    route_key: named.current.routeKey,
                    page_title: named.current.title,
                    duration_seconds: activeSeconds,
                    visited_at: new Date(startedAt).toISOString(),
                    session_id: sessionId(),
                    referrer_path: referrer,
                }),
            }).catch(() => undefined)
        }
        for (const type of INPUT_EVENTS) window.addEventListener(type, onInput, { capture: true, passive: true })
        document.addEventListener('visibilitychange', onVisibility)
        // pagehide: mobile Safari often skips beforeunload when a tab is closed or swiped away.
        window.addEventListener('pagehide', send)
        window.addEventListener('beforeunload', send)
        return () => {
            for (const type of INPUT_EVENTS) window.removeEventListener(type, onInput, { capture: true })
            document.removeEventListener('visibilitychange', onVisibility)
            window.removeEventListener('pagehide', send)
            window.removeEventListener('beforeunload', send)
            send()
            previous.current = pagePath
        }
    }, [enabled, pathname, search])
}
